import { FormEvent, useContext, useEffect, useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { Checkbox, DefaultButton, Dialog, FontIcon, Stack, Text } from '@fluentui/react'
import { useBoolean } from '@fluentui/react-hooks'
import { ThumbDislike20Filled, ThumbLike20Filled } from '@fluentui/react-icons'
import DOMPurify from 'dompurify'
import remarkGfm from 'remark-gfm'
import supersub from 'remark-supersub'
import rehypeRaw from 'rehype-raw'
import { AskResponse, Citation, Feedback, historyMessageFeedback } from '../../api'
import { XSSAllowTags, XSSAllowAttributes } from '../../constants/sanatizeAllowables'
import { AppStateContext } from '../../state/AppProvider'

import { parseAnswer } from './AnswerParser'

import styles from './Answer.module.css'
import { MdOutlineContentCopy } from 'react-icons/md'
import Tooltip from '../../utils/ToolTip'
import toast from 'react-hot-toast'
import { TriangleLoading } from './TriangleLoading'
import { formatDate } from '../../utils/common'
import { Link } from 'react-router-dom'

interface Props {
  answer: AskResponse
  onCitationClicked: (citedDocument: Citation) => void
  onExectResultClicked: (answerId: string) => void
  showLoadingMessage: boolean
  date: string
}

export const Answer = ({ answer, onCitationClicked, onExectResultClicked, showLoadingMessage, date }: Props) => {
  console.log('full answer', answer)
  const initializeAnswerFeedback = (answer: AskResponse) => {
    if (answer.message_id == undefined) return undefined
    if (answer.feedback == undefined) return undefined
    if (answer.feedback.split(',').length > 1) return Feedback.Negative
    if (Object.values(Feedback).includes(answer.feedback)) return answer.feedback
    return Feedback.Neutral
  }

  const [isRefAccordionOpen, { toggle: toggleIsRefAccordionOpen }] = useBoolean(false)
  const filePathTruncationLimit = 50

  const parsedAnswer = useMemo(() => parseAnswer(answer), [answer])
  const [chevronIsExpanded, setChevronIsExpanded] = useState(isRefAccordionOpen)
  const [feedbackState, setFeedbackState] = useState(initializeAnswerFeedback(answer))
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false)
  const [showReportInappropriateFeedback, setShowReportInappropriateFeedback] = useState(false)
  const [negativeFeedbackList, setNegativeFeedbackList] = useState<Feedback[]>([])
  const appStateContext = useContext(AppStateContext)
  const FEEDBACK_ENABLED =
    appStateContext?.state.frontendSettings?.feedback_enabled && appStateContext?.state.isCosmosDBAvailable?.cosmosDB
  const SANITIZE_ANSWER = appStateContext?.state.frontendSettings?.sanitize_answer

  console.log('parsedAnswer from ANSWER', parsedAnswer)

  const replacedText =
    parsedAnswer?.markdownFormatText.replace(
      /\[\[(\d+)\]\]/g,
      (_match, citationNumber) =>
        `<span class="px-1 bg-primary text-white rounded text-[10px]">${citationNumber}</span>`
    ) || ''

  const handleChevronClick = () => {
    setChevronIsExpanded(!chevronIsExpanded)
    toggleIsRefAccordionOpen()
  }

  useEffect(() => {
    setChevronIsExpanded(isRefAccordionOpen)
  }, [isRefAccordionOpen])

  useEffect(() => {
    if (answer.message_id == undefined) return

    let currentFeedbackState
    if (appStateContext?.state.feedbackState && appStateContext?.state.feedbackState[answer.message_id]) {
      currentFeedbackState = appStateContext?.state.feedbackState[answer.message_id]
    } else {
      currentFeedbackState = initializeAnswerFeedback(answer)
    }
    setFeedbackState(currentFeedbackState)
  }, [appStateContext?.state.feedbackState, feedbackState, answer.message_id])

  const createCitationFilepath = (citation: Citation, index: number, truncate: boolean = false) => {
    let citationFilename = ''

    if (citation.filepath) {
      const part_i = citation.part_index ?? (citation.chunk_id ? parseInt(citation.chunk_id) + 1 : '')
      if (truncate && citation.filepath.length > filePathTruncationLimit) {
        const citationLength = citation.filepath.length
        citationFilename = `${citation.filepath.substring(0, 20)}...${citation.filepath.substring(citationLength - 20)} - Part ${part_i}`
      } else {
        citationFilename = `${citation.filepath} - Part ${part_i}`
      }
    } else if (citation.filepath && citation.reindex_id) {
      citationFilename = `${citation.filepath} - Part ${citation.reindex_id}`
    } else {
      citationFilename = `Citation ${index}`
    }
    return citationFilename
  }

  const onLikeResponseClicked = async () => {
    if (answer.message_id == undefined) return

    let newFeedbackState = feedbackState
    // Set or unset the thumbs up state
    if (feedbackState == Feedback.Positive) {
      newFeedbackState = Feedback.Neutral
    } else {
      newFeedbackState = Feedback.Positive
    }
    appStateContext?.dispatch({
      type: 'SET_FEEDBACK_STATE',
      payload: { answerId: answer.message_id, feedback: newFeedbackState }
    })
    setFeedbackState(newFeedbackState)

    // Update message feedback in db
    await historyMessageFeedback(answer.message_id, newFeedbackState)
  }

  const onDislikeResponseClicked = async () => {
    if (answer.message_id == undefined) return

    let newFeedbackState = feedbackState
    if (feedbackState === undefined || feedbackState === Feedback.Neutral || feedbackState === Feedback.Positive) {
      newFeedbackState = Feedback.Negative
      setFeedbackState(newFeedbackState)
      setIsFeedbackDialogOpen(true)
    } else {
      // Reset negative feedback to neutral
      newFeedbackState = Feedback.Neutral
      setFeedbackState(newFeedbackState)
      await historyMessageFeedback(answer.message_id, Feedback.Neutral)
    }
    appStateContext?.dispatch({
      type: 'SET_FEEDBACK_STATE',
      payload: { answerId: answer.message_id, feedback: newFeedbackState }
    })
  }

  const updateFeedbackList = (ev?: FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
    if (answer.message_id == undefined) return
    const selectedFeedback = (ev?.target as HTMLInputElement)?.id as Feedback

    let feedbackList = negativeFeedbackList.slice()
    if (checked) {
      feedbackList.push(selectedFeedback)
    } else {
      feedbackList = feedbackList.filter(f => f !== selectedFeedback)
    }

    setNegativeFeedbackList(feedbackList)
  }

  const onSubmitNegativeFeedback = async () => {
    if (answer.message_id == undefined) return
    await historyMessageFeedback(answer.message_id, negativeFeedbackList.join(','))
    resetFeedbackDialog()
  }

  const resetFeedbackDialog = () => {
    setIsFeedbackDialogOpen(false)
    setShowReportInappropriateFeedback(false)
    setNegativeFeedbackList([])
  }

  const handleCopy = async (answerText: string) => {
    try {
      await navigator.clipboard.writeText(answerText)
      toast.custom(
        <div className="flex items-center bg-background rounded-full p-5">
          <MdOutlineContentCopy className="mr-2 text-primary" size={20} />
          <span className="text-sm text-primary">Text Copied</span>
        </div>
      )
    } catch (err) {
      console.error('Failed to copy!', err)
    }
  }

  const UnhelpfulFeedbackContent = () => {
    return (
      <>
        <div>Why wasn't this response helpful?</div>
        <Stack tokens={{ childrenGap: 4 }}>
          <Checkbox
            label="Citations are missing"
            id={Feedback.MissingCitation}
            defaultChecked={negativeFeedbackList.includes(Feedback.MissingCitation)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Citations are wrong"
            id={Feedback.WrongCitation}
            defaultChecked={negativeFeedbackList.includes(Feedback.WrongCitation)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="The response is not from my data"
            id={Feedback.OutOfScope}
            defaultChecked={negativeFeedbackList.includes(Feedback.OutOfScope)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Inaccurate or irrelevant"
            id={Feedback.InaccurateOrIrrelevant}
            defaultChecked={negativeFeedbackList.includes(Feedback.InaccurateOrIrrelevant)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Other"
            id={Feedback.OtherUnhelpful}
            defaultChecked={negativeFeedbackList.includes(Feedback.OtherUnhelpful)}
            onChange={updateFeedbackList}></Checkbox>
        </Stack>
        <div onClick={() => setShowReportInappropriateFeedback(true)} style={{ color: '#115EA3', cursor: 'pointer' }}>
          Report inappropriate content
        </div>
      </>
    )
  }

  const ReportInappropriateFeedbackContent = () => {
    return (
      <>
        <div>
          The content is <span style={{ color: 'red' }}>*</span>
        </div>
        <Stack tokens={{ childrenGap: 4 }}>
          <Checkbox
            label="Hate speech, stereotyping, demeaning"
            id={Feedback.HateSpeech}
            defaultChecked={negativeFeedbackList.includes(Feedback.HateSpeech)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Violent: glorification of violence, self-harm"
            id={Feedback.Violent}
            defaultChecked={negativeFeedbackList.includes(Feedback.Violent)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Sexual: explicit content, grooming"
            id={Feedback.Sexual}
            defaultChecked={negativeFeedbackList.includes(Feedback.Sexual)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Manipulative: devious, emotional, pushy, bullying"
            defaultChecked={negativeFeedbackList.includes(Feedback.Manipulative)}
            id={Feedback.Manipulative}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Other"
            id={Feedback.OtherHarmful}
            defaultChecked={negativeFeedbackList.includes(Feedback.OtherHarmful)}
            onChange={updateFeedbackList}></Checkbox>
        </Stack>
      </>
    )
  }

  const components = {
    code({ node, ...props }: { node: any; [key: string]: any }) {
      let language
      if (props.className) {
        const match = props.className.match(/language-(\w+)/)
        language = match ? match[1] : undefined
      }
      const codeString = node.children[0].value ?? ''
      return (
        <SyntaxHighlighter style={nord} language={language} PreTag="div" {...props}>
          {codeString}
        </SyntaxHighlighter>
      )
    }
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start gap-3">
        <div style={{ minWidth: '20px', minHeight: '20px' }}>
          <TriangleLoading showLoadingMessage={showLoadingMessage} />
        </div>
        <Stack
          className={`flex flex-col items-start p-3 ${
            date !== '' ? 'gap-1.5' : 'gap-0'
          } bg-[#f2f2f2] text-primary rounded-t-xl rounded-r-xl shadow-md`}
          style={{
            boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)'
          }}
          tabIndex={0}>
          <Stack.Item>
            <Stack horizontal grow>
              <Stack.Item
                grow
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'start',
                  alignItems: 'start',
                  width: '100%'
                }}>
                {parsedAnswer && (
                  <ReactMarkdown
                    linkTarget="_blank"
                    remarkPlugins={[remarkGfm, supersub]}
                    rehypePlugins={[rehypeRaw]}
                    children={
                      SANITIZE_ANSWER
                        ? DOMPurify.sanitize(replacedText, {
                            ALLOWED_TAGS: XSSAllowTags,
                            ALLOWED_ATTR: XSSAllowAttributes
                          })
                        : replacedText
                    }
                    className="text-base font-normal leading-6 m-1 whitespace-normal break-words max-w-full flex-grow"
                    // components={components}
                    components={{
                      p: ({ node, ...props }) => <p className="mt-4" {...props} />
                    }}
                  />
                )}
              </Stack.Item>
              <Stack.Item className={styles.answerHeader}>
                {FEEDBACK_ENABLED && answer.message_id !== undefined && (
                  <Stack horizontal horizontalAlign="space-between">
                    <ThumbLike20Filled
                      aria-hidden="false"
                      aria-label="Like this response"
                      onClick={() => onLikeResponseClicked()}
                      style={
                        feedbackState === Feedback.Positive ||
                        appStateContext?.state.feedbackState[answer.message_id] === Feedback.Positive
                          ? { color: 'darkgreen', cursor: 'pointer' }
                          : { color: 'slategray', cursor: 'pointer' }
                      }
                    />
                    <ThumbDislike20Filled
                      aria-hidden="false"
                      aria-label="Dislike this response"
                      onClick={() => onDislikeResponseClicked()}
                      style={
                        feedbackState !== Feedback.Positive &&
                        feedbackState !== Feedback.Neutral &&
                        feedbackState !== undefined
                          ? { color: 'darkred', cursor: 'pointer' }
                          : { color: 'slategray', cursor: 'pointer' }
                      }
                    />
                  </Stack>
                )}
              </Stack.Item>
            </Stack>
          </Stack.Item>

          <Stack horizontal className={styles.answerFooter}>
            {date !== '' && (
              <div className={`flex gap-5 w-full justify-between relative`}>
                <span className="text-xs leading-4 pr-5 font-normal flex items-center text-start text-black">
                  {formatDate(date)}
                </span>

                <button
                  onClick={() => parsedAnswer?.markdownFormatText && handleCopy(parsedAnswer.markdownFormatText)}
                  className="flex items-center gap-2 ml-5">
                  <Tooltip content={'Copy Response'}>
                    <MdOutlineContentCopy className="text-xl cursor-pointer text-black" />
                  </Tooltip>
                </button>
              </div>
            )}
            {!!answer.exec_results?.length && (
              <Stack.Item onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? toggleIsRefAccordionOpen() : null)}>
                <Stack style={{ width: '100%' }}>
                  <Stack horizontal horizontalAlign="start" verticalAlign="center">
                    <Text
                      className={styles.accordionTitle}
                      onClick={() => onExectResultClicked(answer.message_id ?? '')}
                      aria-label="Open Intents"
                      tabIndex={0}
                      role="button">
                      <span>Show Intents</span>
                    </Text>
                    <FontIcon className={styles.accordionIcon} onClick={handleChevronClick} iconName={'ChevronRight'} />
                  </Stack>
                </Stack>
              </Stack.Item>
            )}
          </Stack>
        </Stack>
        <Dialog
          onDismiss={() => {
            resetFeedbackDialog()
            setFeedbackState(Feedback.Neutral)
          }}
          hidden={!isFeedbackDialogOpen}
          styles={{
            main: [
              {
                selectors: {
                  ['@media (min-width: 480px)']: {
                    maxWidth: '600px',
                    background: '#FFFFFF',
                    boxShadow: '0px 14px 28.8px rgba(0, 0, 0, 0.24), 0px 0px 8px rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                    maxHeight: '600px',
                    minHeight: '100px'
                  }
                }
              }
            ]
          }}
          dialogContentProps={{
            title: 'Submit Feedback',
            showCloseButton: true
          }}>
          <Stack tokens={{ childrenGap: 4 }}>
            <div>Your feedback will improve this experience.</div>

            {!showReportInappropriateFeedback ? <UnhelpfulFeedbackContent /> : <ReportInappropriateFeedbackContent />}

            <div>By pressing submit, your feedback will be visible to the application owner.</div>

            <DefaultButton disabled={negativeFeedbackList.length < 1} onClick={onSubmitNegativeFeedback}>
              Submit
            </DefaultButton>
          </Stack>
        </Dialog>
      </div>
      {parsedAnswer?.generated_chart !== null && (
        <Stack className={styles.answerContainer}>
          <Stack.Item grow>
            <img src={`data:image/png;base64, ${parsedAnswer?.generated_chart}`} />
          </Stack.Item>
        </Stack>
      )}
      {!!parsedAnswer?.citations.length && (
        <Stack.Item onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? toggleIsRefAccordionOpen() : null)}>
          <Stack style={{ width: '100%' }}>
            <Stack horizontal horizontalAlign="start" verticalAlign="center">
              <Text
                className="mr-[5px] font-normal text-sm leading-4 flex items-center text-gray-600 capitalize"
                onClick={toggleIsRefAccordionOpen}
                aria-label="Open references"
                tabIndex={0}
                role="button">
                <span>Reference</span>
              </Text>
            </Stack>
          </Stack>
        </Stack.Item>
      )}
      <div className="mt-2 flex flex-col flex-wrap max-h-full md:max-h-[150px] gap-1">
        {parsedAnswer?.citations.map((citation, idx) => {
          return (
            <span
              title={createCitationFilepath(citation, ++idx)}
              tabIndex={0}
              role="link"
              key={idx}
              onClick={() => onCitationClicked(citation)}
              onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? onCitationClicked(citation) : null)}
              className="md:ml-[10px] font-semibold text-xs leading-4 text-[#115ea3] flex flex-row items-center px-1.5 py-1 gap-1 border border-[#d1d1d1] rounded w-fit cursor-pointer"
              aria-label={createCitationFilepath(citation, idx)}>
              <div className="box-border inline-flex flex-col justify-center items-center p-0 w-[14px] h-[14px] border border-gray-300 rounded flex-none flex-grow-0 text-semibold text-[10px] leading-[14px] text-center text-[#424242] cursor-pointer hover:underline">
                {idx}
              </div>
              {/* {createCitationFilepath(citation, idx, true)} */}
              {citation?.title}
            </span>
          )
        })}
      </div>
    </div>
  )
}
